import { onBeforeUnmount, ref } from 'vue'

export type PointerDragAxis = 'x' | 'y' | 'both'

export interface PointerDragPosition {
  x: number
  y: number
}

export interface UsePointerDragOptions {
  /** 拖动轴向：x 仅左右，y 仅上下，both 自由拖动 */
  axis?: PointerDragAxis
  /** 长按后开始拖动，0 表示移动超过阈值即拖动 */
  longPressMs?: number
  /** 移动超过该距离后取消长按 / 判定为拖动 */
  moveThreshold?: number
  initialX?: number
  initialY?: number
  clampX?: (value: number) => number
  clampY?: (value: number) => number
  /** y 为 bottom 定位时可设为 true：手指上移 y 增大 */
  invertYDelta?: boolean
  onDragEnd?: (position: PointerDragPosition) => void
}

export function usePointerDrag(options: UsePointerDragOptions = {}) {
  const {
    axis = 'both',
    longPressMs = 0,
    moveThreshold = 10,
    initialX = 0,
    initialY = 0,
    clampX = (value) => value,
    clampY = (value) => value,
    invertYDelta = false,
    onDragEnd,
  } = options

  const x = ref(initialX)
  const y = ref(initialY)
  const isDragging = ref(false)
  const suppressClick = ref(false)

  const canMoveX = axis === 'x' || axis === 'both'
  const canMoveY = axis === 'y' || axis === 'both'

  let longPressTimer: number | null = null
  let pointerTracking = false
  let documentListening = false
  let startPointerX = 0
  let startPointerY = 0
  let startX = 0
  let startY = 0

  function clearLongPressTimer() {
    if (longPressTimer) {
      clearTimeout(longPressTimer)
      longPressTimer = null
    }
  }

  function removeDocumentListeners() {
    if (!documentListening) return
    document.removeEventListener('pointermove', onDocumentPointerMove)
    document.removeEventListener('pointerup', onDocumentPointerUp)
    document.removeEventListener('pointercancel', onDocumentPointerUp)
    documentListening = false
  }

  function addDocumentListeners() {
    if (documentListening) return
    document.addEventListener('pointermove', onDocumentPointerMove, { passive: false })
    document.addEventListener('pointerup', onDocumentPointerUp)
    document.addEventListener('pointercancel', onDocumentPointerUp)
    documentListening = true
  }

  function applyPosition(clientX: number, clientY: number) {
    const deltaX = clientX - startPointerX
    const deltaY = clientY - startPointerY
    if (canMoveX) {
      x.value = clampX(startX + deltaX)
    }
    if (canMoveY) {
      const delta = invertYDelta ? -deltaY : deltaY
      y.value = clampY(startY + delta)
    }

    if (Math.abs(deltaX) > moveThreshold || Math.abs(deltaY) > moveThreshold) {
      suppressClick.value = true
    }
  }

  function onDocumentPointerMove(event: PointerEvent) {
    if (!pointerTracking) return

    if (!isDragging.value) {
      if (longPressMs > 0) return
      const deltaX = event.clientX - startPointerX
      const deltaY = event.clientY - startPointerY
      if (Math.hypot(deltaX, deltaY) < moveThreshold) return
      isDragging.value = true
      suppressClick.value = true
    }

    event.preventDefault()
    applyPosition(event.clientX, event.clientY)
  }

  function onDocumentPointerUp() {
    clearLongPressTimer()
    if (!pointerTracking) return

    const wasDragging = isDragging.value
    pointerTracking = false
    isDragging.value = false
    removeDocumentListeners()

    if (wasDragging) {
      onDragEnd?.({ x: x.value, y: y.value })
      window.setTimeout(() => {
        suppressClick.value = false
      }, 0)
      return
    }

    suppressClick.value = false
  }

  function onPointerDown(event: PointerEvent) {
    if (event.button !== 0) return

    pointerTracking = true
    isDragging.value = false
    suppressClick.value = false
    startPointerX = event.clientX
    startPointerY = event.clientY
    startX = x.value
    startY = y.value

    clearLongPressTimer()

    if (longPressMs > 0) {
      longPressTimer = window.setTimeout(() => {
        isDragging.value = true
        suppressClick.value = true
        addDocumentListeners()
      }, longPressMs)
      return
    }

    addDocumentListeners()
  }

  function onPointerMove(event: PointerEvent) {
    if (isDragging.value || !pointerTracking || longPressMs === 0) return

    const deltaX = Math.abs(event.clientX - startPointerX)
    const deltaY = Math.abs(event.clientY - startPointerY)
    const moved =
      (canMoveX && deltaX > moveThreshold) || (canMoveY && deltaY > moveThreshold)

    if (moved) {
      clearLongPressTimer()
    }
  }

  function onPointerUp() {
    clearLongPressTimer()
    if (!isDragging.value) {
      pointerTracking = false
      suppressClick.value = false
    }
  }

  function stopDragging() {
    clearLongPressTimer()
    pointerTracking = false
    isDragging.value = false
    removeDocumentListeners()
  }

  onBeforeUnmount(stopDragging)

  return {
    x,
    y,
    isDragging,
    suppressClick,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    stopDragging,
  }
}
